import { useState, useEffect, useRef } from 'react';

/**
 * @hook useProjectDataForm
 * @description Hook that manages the state and logic for the ProjectDataForm component.
 * @param {Object} props
 * @param {Object} props.initialData - The initial project data
 * @param {string} props.mode - The current mode ('view', 'edit', 'create')
 * @param {Array} props.projects - List of active projects to check for conflicts
 * @param {Array} props.deletedProjects - List of deleted projects to check for conflicts
 * @param {Function} props.onSubmit - Callback when form is valid and submitted
 */
export function useProjectDataForm({ initialData, mode, projects = [], deletedProjects = [], onSubmit }) {
    const [form, setForm] = useState({
        name: '', project_number: '', address: '', description: '', status: 'active',
        latitude: null, longitude: null, customer_id: null
    });
    const [currentMode, setCurrentMode] = useState(mode);
    const [searchQuery, setSearchQuery] = useState('');
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [projectNumberError, setProjectNumberError] = useState('');
    const [nameError, setNameError] = useState('');
    const projectNumberTimerRef = useRef(null);

    useEffect(() => {
        if (initialData) {
            setForm({
                ...initialData,
                latitude: initialData.latitude ? parseFloat(initialData.latitude) : null,
                longitude: initialData.longitude ? parseFloat(initialData.longitude) : null
            });
        }
        setCurrentMode(mode);
        setSearchQuery(initialData?.address || '');
    }, [initialData, mode]);

    const findNumberConflict = (projectNumber) => {
        const normalized = String(projectNumber || '').trim();
        if (!normalized) return null;

        const activeConflict = projects.find(p => String(p.project_number || '').trim() === normalized && p.id !== form.id);
        if (activeConflict) {
            return { type: 'active', project: activeConflict };
        }

        const deletedConflict = deletedProjects.find(p => String(p.project_number || '').trim() === normalized && p.id !== form.id);
        if (deletedConflict) {
            return { type: 'deleted', project: deletedConflict };
        }

        return null;
    };

    useEffect(() => {
        if (projectNumberTimerRef.current) {
            clearTimeout(projectNumberTimerRef.current);
        }

        if (currentMode === 'view') {
            return undefined;
        }

        projectNumberTimerRef.current = setTimeout(() => {
            const projectNumber = form.project_number?.trim();
            if (!projectNumber) {
                setProjectNumberError('');
                return;
            }

            const conflict = findNumberConflict(projectNumber);
            if (!conflict) {
                setProjectNumberError('');
                return;
            }

            setProjectNumberError(conflict.type === 'deleted'
                ? 'Diese Projektnummer ist durch ein Projekt im Papierkorb belegt.'
                : 'Diese Projektnummer existiert bereits. Bitte eine andere wählen.');
        }, 350);

        return () => {
            if (projectNumberTimerRef.current) {
                clearTimeout(projectNumberTimerRef.current);
            }
        };
    }, [form.project_number, currentMode, projects, form.id]);

    useEffect(() => {
        const projectName = form.name?.trim();
        if (!projectName) {
            setNameError('');
            return;
        }

        const duplicateName = projects.find(p => p.name?.toLowerCase() === projectName.toLowerCase() && p.id !== form.id);
        setNameError(duplicateName ? `Ein Projekt mit dem Namen "${form.name}" existiert bereits.` : '');
    }, [form.name, projects, form.id]);

    const validateAndSubmit = () => {
        const isNumEmpty = !form.project_number?.trim();
        const isNameEmpty = !form.name?.trim();
        const duplicateNum = findNumberConflict(form.project_number);
        const duplicateName = projects.find(p => p.name?.toLowerCase() === form.name?.trim().toLowerCase() && p.name?.trim() !== '' && p.id !== form.id);

        if (isNumEmpty || isNameEmpty) {
            setAttemptedSubmit(true);
            return false;
        }

        if (duplicateNum) {
            setAttemptedSubmit(true);
            setProjectNumberError(duplicateNum.type === 'deleted'
                ? 'Diese Projektnummer ist durch ein Projekt im Papierkorb belegt.'
                : 'Diese Projektnummer existiert bereits. Bitte eine andere wählen.');
            return false;
        }
        if (duplicateName) {
            if (!window.confirm(`Ein Projekt mit dem Namen "${form.name}" existiert bereits. Möchtest du diesen Namen trotzdem verwenden?`)) {
                return false;
            }
        }

        onSubmit(form, currentMode);
        return true;
    };

    const formatAddress = (addr) => {
        if (!addr) return '';
        const street = addr.road || addr.pedestrian || addr.suburb || '';
        const houseNumber = addr.house_number || '';
        const city = addr.city || addr.town || addr.village || addr.municipality || '';
        const postcode = addr.postcode || '';
        const country = addr.country || '';

        const parts = [];
        if (street) parts.push(houseNumber ? `${street} ${houseNumber}` : street);
        if (city) parts.push(postcode ? `${postcode} ${city}` : city);
        if (country) parts.push(country);

        return parts.join(', ');
    };

    const fetchAddress = async (lat, lng) => {
        try {
            const token = process.env.REACT_APP_LOCATIONIQ_TOKEN;
            const res = await fetch(`https://eu1.locationiq.com/v1/reverse.php?key=${token}&lat=${lat}&lon=${lng}&format=json`);
            const data = await res.json();
            if (data && data.address) {
                const formatted = formatAddress(data.address);
                setForm(prev => ({ ...prev, address: formatted }));
                setSearchQuery(formatted);
            }
        } catch (err) {
            console.error('Reverse Geocoding Error:', err);
        }
    };

    const handleLocationSelect = (latlng, displayName) => {
        setForm(prev => ({
            ...prev,
            latitude: latlng.lat,
            longitude: latlng.lng,
            address: displayName || prev.address
        }));
        if (displayName) setSearchQuery(displayName);
        if (!displayName) fetchAddress(latlng.lat, latlng.lng);
    };

    return {
        form,
        setForm,
        currentMode,
        setCurrentMode,
        searchQuery,
        setSearchQuery,
        attemptedSubmit,
        setAttemptedSubmit,
        projectNumberError,
        setProjectNumberError,
        nameError,
        setNameError,
        validateAndSubmit,
        handleLocationSelect
    };
}
